/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { atom } from 'jotai';
import { PitStrategyData, SanctioningBodyEnum } from 'models/viewModels';
import { pitStrategyDataService } from 'services/stream/RSocket';
import { activeEventSelector, activeSessionReplayEnabledSelector, activeSessionSelector } from 'jotai/session';
import { unwrap } from 'jotai/utils';
import { lapReplayControllerAtom } from 'jotai/raceReplay';
import { activeSanctioningBodyIdAtom } from 'jotai/sanctioningBody';
import { historicalRaceServiceServer } from 'services/historicalRace/HistoricalRaceService';

let listenerId: string;
const currentPitStrategyAtom = atom<PitStrategyData[]>([]);
const initPitStrategyAtom = atom<null, [], () => void>(null, (get, set) => {
  const sanctioningBody = get(activeSanctioningBodyIdAtom) ?? SanctioningBodyEnum.NASCAR;
  listenerId = pitStrategyDataService.get(sanctioningBody)!.addListener(
    async (pitStrategy) => {
      set(currentPitStrategyAtom, pitStrategy);
    },
    async () => {
      set(currentPitStrategyAtom, []);
    }
  );
  return () => {
    pitStrategyDataService.get(sanctioningBody)!.removeListener(listenerId);
  };
});

initPitStrategyAtom.onMount = (init) => {
  init();
};

export const pitStrategyAtom = atom<PitStrategyData[]>((get) => {
  get(initPitStrategyAtom);
  return get(currentPitStrategyAtom);
});

export const pitStrategySelectorFamily = atom<PitStrategyData[]>((get) => {
  const isReplay = get(activeSessionReplayEnabledSelector);
  return isReplay ? get(pitStrategyReplayCurrentLapDataSelector) : get(pitStrategyAtom);
});

const pitStrategyReplayDataSelector = atom<Promise<PitStrategyData[] | undefined>>(async (get) => {
  const eventId = get(activeEventSelector)?.eventId;
  const sessionType = get(activeSessionSelector)?.sessionType;
  const sessionId = get(activeSessionSelector)?.sessionId;
  return eventId && sessionType && sessionId
    ? await historicalRaceServiceServer.getReplayPitStrategyData(eventId, sessionType, sessionId)
    : [];
});

const unwrappedPitStrategyReplayDataSelector = unwrap(pitStrategyReplayDataSelector);

const pitStrategyReplayCurrentLapDataSelector = atom<PitStrategyData[]>((get) => {
  const currentLap = get(lapReplayControllerAtom);
  const allData = get(unwrappedPitStrategyReplayDataSelector);
  const pitStratData = allData?.filter((item) => item.leaderLap <= currentLap);
  return pitStratData ? pitStratData.flatMap((item) => item) : [];
});
